import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Button,
  Icon,
  InputText,
  Notifications,
  Progress,
} from "d-react-components";
import { useLocalStorage } from "react-use";
// import { useAdminLoginMutation } from "../../network/hooks";
import { useTranslation } from "react-i18next";
import { isEmpty } from "lodash";
import { AUTH_ERRORS } from "../../constants/errors";
import { ADMIN_AUTH_KEY, ADMIN_TOKEN_KEY } from "@constants";
import {
  getAdminProfile,
  getWorkinfinityXLoginURL,
  login,
  loginWorkinfinityX,
  validateAuth,
} from "@network/api/auth";
import { StringParam, useQueryParam } from "use-query-params";
// import { adminLogin } from "../../network/auth";

interface LoginProps {
  [key: string]: any;
}

interface LoginForm {
  username?: string;
  password?: string;
  authenticator?: string;
  token?: string;
}

type LoginType = "default" | "password" | "workinfinityx";

export const Login: React.FC<LoginProps> = () => {
  const [loginType, setLoginType] = useState<LoginType>("default");
  const [workinfinityXUrl, setWorkinfinityXUrl] = useState();
  const [thirdPartyAccessToken] = useQueryParam("code", StringParam);
  const [, setAdminInfo] = useLocalStorage<any>(ADMIN_AUTH_KEY, {});

  const { t } = useTranslation();

  // useEffect(() => {
  //   getWorkinfinityXLoginURL().then(
  //     (resp) => {
  //       setWorkinfinityXUrl(resp?.data?.data?.url);
  //     },
  //     (err) => {
  //       console.log(err);
  //     }
  //   );
  // }, []);

  useEffect(() => {
    if (thirdPartyAccessToken) {
      setTimeout(() => {
        Progress.show(
          {
            method: loginWorkinfinityX,
            params: { code: thirdPartyAccessToken },
          },
          (resp: any) => {
            const adminProfile = resp?.data?.data?.profile ?? {};
            if (!isEmpty(adminProfile)) {
              setAdminInfo(adminProfile);
              Notifications.showSuccess(t("notification:loginSuccessfully"));
              window.location.replace("/");
            }
          },
          (err: any) => {
            Notifications.showError(err);
          }
        );
      }, 500);
    }
  }, [thirdPartyAccessToken]);

  return (
    <div className="bg-gray-100 w-full">
      <div className="flex items-center justify-center min-h-screen">
        <div className="h-[500px] overflow-hidden">
          <img
            alt=""
            src={"/images/login-welcome.jpg"}
            className="w-[500px] h-[500px] mb-4 object-cover"
          />
        </div>
        <div className="max-w-[350px] h-[500px] w-full bg-white p-6 text-left flex items-center relative">
          <div className="w-100">
            <div className="w-100 py-3 d-flex flex-columns align-items-center justify-content-center">
              <img
                alt=""
                src={"/images/logos/logo_login.png"}
                className="mb-4"
                style={{ maxWidth: "100px", maxHeight: "100px" }}
              />
            </div>
            {loginType === "default" && (
              <div>
                <a
                  href={workinfinityXUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text text-nowrap d-button d-button__large w-100 inline-block text-white"
                  style={{backgroundColor: "#041B47"}}
                >
                  <div className="bg-white w-6 h-6 p-1 rounded-1 mr-3">
                    <img
                      src="/images/workInfinityLogo.png"
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <span className="text-white">
                    {t("loginWithWorkInfinityX")}
                  </span>
                </a>
                <div className="relative my-3 text-center">
                  <div className="border-t-[1px] absolute w-full top-[18px] z-10"></div>
                  <span className="py-2 relative z-20 px-2 bg-white inline-block">
                    {t("orLoginWith")}
                  </span>
                </div>
                <Button
                  onClick={() => setLoginType("password")}
                  variant="outline"
                  className="w-100"
                >
                  {t("loginWithUsernamePassword")}
                </Button>
              </div>
            )}
            {loginType === "password" && (
              <LoginDefault setLoginType={setLoginType} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const LoginDefault = ({
  setLoginType,
}: {
  setLoginType: (type: LoginType) => void;
}) => {
  const [showAuthenticationView, setShowAuthenticationView] = useState(false);
  const [, setToken] = useLocalStorage<any>(ADMIN_TOKEN_KEY, "", { raw: true });
  const [, setAdminInfo] = useLocalStorage<any>(ADMIN_AUTH_KEY, {});
  const { t } = useTranslation();

  const getAdminInfo = async () => {
    setTimeout(async () => {
      try {
        const adminProfileRes = await getAdminProfile();
        const adminProfile = adminProfileRes?.data?.data?.profile ?? {};
        if (!isEmpty(adminProfile)) {
          setAdminInfo(adminProfile);
          Notifications.showSuccess(t("notification:loginSuccessfully"));
          window.location.replace("/");
        }
      } catch (err) {
        Notifications.showError(err);
      }
    }, 500);
  };

  const LoginSchema = Yup.object().shape({
    username: Yup.string().required("This field is required"),
    password: Yup.string().required("This field is required"),
  });
  const loginForm = useFormik<LoginForm>({
    initialValues: {},
    validationSchema: LoginSchema,
    onSubmit: (e) => {
      const { username, password, authenticator, token } = values;
      if (!showAuthenticationView && username && password) {
        login({
          username,
          password,
        }).then(
          (resp) => {
            const loggedInData = resp?.data?.data;
            if (!isEmpty(loggedInData)) {
              const { authenticator } = loggedInData;
              if (!authenticator) {
                setShowAuthenticationView(true);
                setFieldValue("token", loggedInData.api_token);
                return;
              } else {
                setToken(loggedInData.api_token);
                getAdminInfo();
              }
            }
          },
          (error) => {
            Notifications.showError(error);
          }
        );
      } else if (showAuthenticationView) {
        validateAuth({
          token,
          code: authenticator,
        }).then(
          (resp) => {
            const loggedInData = resp?.data?.data;
            if (!isEmpty(loggedInData)) {
              const { authenticator } = loggedInData;
              if (authenticator) {
                setToken(loggedInData.api_token);
                getAdminInfo();
              } else {
                Notifications.showError(
                  t(`notification:${resp?.data?.message}`)
                );
              }
            }
          },
          (error) => {
            Notifications.showError(error);
          }
        );
      }
    },
  });
  const { values, errors, handleSubmit, handleChange, setFieldValue } =
    loginForm;

  return (
    <div className="w-100">
      {!showAuthenticationView && (
        <>
          <span
            className="flex cursor-pointer items-center absolute top-3 left-3"
            onClick={() => setLoginType("default")}
          >
            <Icon name="chevron_left" />
            {t("common:back")}
          </span>
          <InputText
            name="username"
            label={"Username"}
            placeholder={"Username"}
            value={values.username}
            error={errors.username}
            onChange={handleChange}
            classNameInput="w-full"
            className="mb-3"
          />
          <InputText
            name="password"
            label={"Password"}
            placeholder={"Password"}
            value={values.password}
            error={errors.password}
            onChange={handleChange}
            classNameInput="w-full"
            className="mb-3"
            type="password"
          />
        </>
      )}
      {showAuthenticationView && (
        <>
          <span
            className="flex cursor-pointer items-center absolute top-3 left-3"
            onClick={() => setShowAuthenticationView(false)}
          >
            <Icon name="chevron_left" />
            {t("common:back")}
          </span>
          <InputText
            name="authenticator"
            label="2FA Authentication"
            value={values?.authenticator}
            error={errors?.authenticator}
            onChange={handleChange}
            classNameInput="w-full"
            className="mb-3"
          />
        </>
      )}
      <Button
        color="primary"
        variant="standard"
        onClick={() => handleSubmit()}
        title={`login`}
        className="w-100 mt-4"
        type="button"
      >
        Login
      </Button>
    </div>
  );
};

export default Login;
